import {
  ActiveBranding,
  Branding,
  Contact,
  EntityTypes,
  PanelRootContext,
  Property,
  PropertyGroup,
  Status,
  TraxeroActionResponse,
} from 'types';
import {
  BASELINE_PROPERTY_BRANDING,
  DEFAULT_BRANDING,
  getContrastColor,
} from 'context/themeContext';
import {
  ChangeEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  FormControl,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  ThemeProvider,
  Tooltip,
  Typography,
} from '@mui/material';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import { useFetcher, useOutletContext, useRevalidator } from 'react-router';
import { AppContext } from 'context/appContext';
import ColorPicker from 'components/inputs/ColorPicker';
import { PermitPortalPreview } from './PermitPortalPreview';
import { PropertiesActionHandler } from 'pages/properties/PropertyLayout';
import { PropertyGroupsActionHandler } from 'pages/property-groups/PropertyGroupLayout';
import AuturaSwitch from 'components/inputs/AuturaSwitch';
import { useTranslation } from 'react-i18next';
import { FMCPreview } from './FMCPreview';
import {
  getBrandedTheme as getAuturaTheme,
  DEFAULT_BRANDING as AUTURA_DEFAULT_BRANDING,
} from 'context/themeContext';
import { ValidatorForm } from 'react-material-ui-form-validator';
import FormFrame from 'components/layout/FormFrame';
import DetailsHeader from 'components/layout/DetailsHeader';

import AuturaDropdown from 'components/inputs/AuturaDropdown';
import AuturaTextField from 'components/inputs/AuturaTextField';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import { routeWithParams } from 'router/routes';
import { ROUTES } from 'router/routes';

export const PropertyBranding = (): ReactNode => {
  const fetcher = useFetcher();
  const displayToast = useContext(ToastContext);
  const revalidator = useRevalidator();

  const { state } = useContext(AppContext);
  const { t } = useTranslation();
  const [preview, setPreview] = useState<'findMyCar' | 'permitPortal'>(
    state.features.permitService ? 'permitPortal' : 'findMyCar',
  );

  const { entity, displayMode } =
    useOutletContext<PanelRootContext<Property | PropertyGroup>>();

  const [branding, setBranding] = useState<Branding>(
    BASELINE_PROPERTY_BRANDING,
  );
  const [brandingOptions, setBrandingOptions] = useState<ActiveBranding[]>([]);

  const [useOtherContactInfo, setUseOtherContactInfo] =
    useState<boolean>(false);

  const [brandingOption, setBrandingOption] = useState<ActiveBranding>({
    id: 0,
    name: entity.name,
    entityType:
      displayMode === 'property'
        ? EntityTypes.Property
        : displayMode === 'group'
          ? EntityTypes.PropertyGroup
          : EntityTypes.Tenant,
    branding: BASELINE_PROPERTY_BRANDING,
    location: entity.location || {},
    contact: entity.contacts[0],
  });

  const canEdit = useMemo(() => {
    if (displayMode === 'property') {
      return state.entityPermissions.property.update;
    } else if (displayMode === 'group') {
      return state.entityPermissions.property_group.update;
    } else if (displayMode === 'tenant') {
      return state.entityPermissions.tenant.update;
    }
    return false;
  }, [displayMode, state.entityPermissions]);

  useEffect(() => {
    setFormState(entity);
  }, []);

  const setFormState = useCallback(
    (entity: Property | PropertyGroup) => {
      const _branding = {
        primary:
          entity.data?.branding?.primary || BASELINE_PROPERTY_BRANDING.primary,
        secondary:
          entity.data?.branding?.secondary ||
          BASELINE_PROPERTY_BRANDING.secondary,
        logoUrl:
          entity.data?.branding?.logoUrl || BASELINE_PROPERTY_BRANDING.logoUrl,
        logoPadding:
          entity.data?.branding?.logoPadding ||
          BASELINE_PROPERTY_BRANDING.logoPadding,
        bannerLogoUrl:
          entity.data?.branding?.bannerLogoUrl ||
          BASELINE_PROPERTY_BRANDING.bannerLogoUrl,
        bannerPadding:
          entity.data?.branding?.bannerPadding ||
          BASELINE_PROPERTY_BRANDING.bannerPadding,
      };
      setBranding(_branding);
      if (displayMode === 'property') {
        const property = entity as Property;

        const options: ActiveBranding[] = [
          {
            id: state.tenant.id,
            name: state.tenant.name,
            entityType: EntityTypes.Tenant,
            branding: state.tenant.data?.branding || DEFAULT_BRANDING,
            location: state.tenant.publicContactInfo?.location,
            contact: state.tenant.publicContactInfo,
          },
          {
            id: 0,
            name: property.name,
            entityType: EntityTypes.Property,
            branding: _branding,
            location: property.location,
            contact: property.contacts[0],
          },
        ];

        if (property.data?.activeBranding?.name) {
          setBrandingOption(property.data.activeBranding);
        } else {
          setBrandingOption(options[0]);
          setUseOtherContactInfo(true);
        }

        if (
          property.data?.activeBranding?.contact &&
          property.data?.activeBranding?.location
        ) {
          setUseOtherContactInfo(true);
        }

        const groupOptions =
          property.propertyGroups?.map(
            (pg: PropertyGroup): ActiveBranding => ({
              id: pg.id,
              name: pg.name,
              entityType: EntityTypes.PropertyGroup,
              branding: pg.data?.branding || BASELINE_PROPERTY_BRANDING,
              location: pg.location || {},
              contact: pg.primaryContact as Contact,
            }),
          ) || [];

        if (property.data?.activeBranding?.id !== 0) {
          const found = options.findIndex(
            (o) => o.id === property?.data?.activeBranding?.id,
          );
          if (found === -1) {
            // TODO: handle case where active branding is from a group which the property no longer belongs to
          }
        }

        setBrandingOptions([...options, ...groupOptions]);
      }
    },
    [displayMode, entity, state.tenant.id, state.tenant.name],
  );

  useEffect(() => {
    if (fetcher.data) {
      const response =
        fetcher.data as TraxeroActionResponse<PropertiesActionHandler>;
      if (response.status < 400) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: t('update_success', {
            ns: 'toasts',
            name: `${entity.name} ${t('branding')}`,
          }),
        });
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: t('update_error', {
            ns: 'toasts',
            name: `${entity.name} ${t('branding')}`,
          }),
        });
      }
    }
  }, [fetcher.data]);

  const handleSubmit = () => {
    if (displayMode === 'group') {
      const group = { ...entity } as PropertyGroup;
      const updated: PropertyGroup = {
        ...group,
        data: {
          ...group.data,
          branding: branding,
        },
      };
      fetcher.submit(
        {
          body: JSON.stringify(updated),
          handler: PropertyGroupsActionHandler.UPDATE,
          tenantID: state.tenant.id,
          groupID: entity.id,
        },
        {
          method: 'PUT',
          encType: 'application/json',
        },
      );
    }

    if (displayMode === 'property') {
      const property = { ...entity } as Property;
      const updated: Property = {
        ...property,
        data: {
          ...property.data,
          branding: branding,
          activeBranding: {
            id: brandingOption.id,
            name: brandingOption.name,
            entityType: brandingOption.entityType,
            branding: brandingOption.branding,
            location: brandingOption.location,
            contact: brandingOption.contact,
          },
        },
      };

      if (useOtherContactInfo && updated.data?.activeBranding) {
        updated.data.activeBranding.contact = brandingOption.contact;
        updated.data.activeBranding.location = brandingOption.location;
      }

      fetcher.submit(
        {
          body: JSON.stringify(updated),
          handler: PropertiesActionHandler.UPDATE,
        },
        {
          method: 'PUT',
          action: routeWithParams(ROUTES.PROPERTY, [
            state.tenant.id,
            entity.id,
          ]),
          encType: 'application/json',
        },
      );
    }
  };

  const handleCancelAction = () => {
    // reset form state
    setFormState(entity);
    revalidator.revalidate();
  };

  const handlePreviewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPreview(value as 'findMyCar' | 'permitPortal');
  };

  const handleBrandingOptionChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const b = brandingOptions.find((b) => b.name === value);
    if (b && b.id !== brandingOption.id) {
      setBrandingOption(b);
      setUseOtherContactInfo(false);
    }
  };

  const handleUseOtherContactInfoChange = (value: boolean) => {
    setUseOtherContactInfo(value);
  };

  const previewTheme = useMemo(() => {
    const theme = getAuturaTheme(AUTURA_DEFAULT_BRANDING);
    if (brandingOption.id !== 0) {
      const primaryColor =
        brandingOption.branding.primary || AUTURA_DEFAULT_BRANDING.primary;
      theme.palette.primary.main = primaryColor;
      theme.palette.primary.contrastText = getContrastColor(primaryColor);
    } else {
      const primaryColor = branding.primary || AUTURA_DEFAULT_BRANDING.primary;
      theme.palette.primary.main = primaryColor;
      theme.palette.primary.contrastText = getContrastColor(primaryColor);
    }
    return theme;
  }, [brandingOption.id, branding.primary]);

  // const activeBrandingEntity = useMemo(() => {
  //   if (displayMode === 'group' || brandingOption.id !== 0) {
  //     return t('property_group.label', { ns: 'entities' }).toLowerCase();
  //   }
  //   return t('property.label', { ns: 'entities' }).toLowerCase();
  // }, [brandingOption.id, displayMode]);

  const publicContactInfo: Contact = useMemo(() => {
    if (useOtherContactInfo) {
      return {
        id: brandingOption.contact?.id || 0,
        name: brandingOption.contact?.name || '',
        status: brandingOption.contact?.status || Status.Active,
        primaryEmail: brandingOption.contact?.primaryEmail || '',
        primaryPhone: brandingOption.contact?.primaryPhone || '',
        location: {
          ...brandingOption.location,
        },
        tenantID: state.tenant.id,
      };
    }

    return {
      id: 0,
      name: entity.name,
      status: Status.Active,
      primaryEmail: entity.contacts[0]?.primaryEmail || '',
      primaryPhone: entity.contacts[0]?.primaryPhone || '',
      location: entity.location || {},
      tenantID: state.tenant.id,
    };
  }, [
    useOtherContactInfo,
    brandingOption.contact?.id,
    brandingOption.contact?.name,
    brandingOption.contact?.primaryEmail,
    brandingOption.contact?.primaryPhone,
    brandingOption.location,
    entity.name,
    entity.location,
    state.tenant.id,
  ]);

  const permitPortalPreviewLogo = useMemo(() => {
    if (brandingOption.id !== 0) {
      return {
        url: useOtherContactInfo
          ? brandingOption.branding.bannerLogoUrl
          : branding.bannerLogoUrl,
        padding: useOtherContactInfo
          ? brandingOption.branding.bannerPadding
          : branding.bannerPadding,
      };
    }

    return {
      url: branding.bannerLogoUrl,
      padding: branding.bannerPadding,
    };
  }, [
    brandingOption.id,
    useOtherContactInfo,
    branding.bannerLogoUrl,
    branding.bannerPadding,
  ]);

  return (
    <ValidatorForm onSubmit={handleSubmit}>
      <FormFrame loading={fetcher.state !== 'idle'}>
        <DetailsHeader
          title={t('tenant.data.branding.label', { ns: 'entities' })}
          submitLabel={t('buttons.update', { name: t('branding') })}
          onCancel={handleCancelAction}
          disableSubmit={fetcher.state !== 'idle'}
          readonly={!canEdit}
        />
        <ScrollableFormContainer maxHeight='calc(100dvh - 454px)'>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            display={'flex'}
            gap={4}
            sx={{ mt: 3 }}
          >
            <Stack direction={'column'} flex={2} spacing={{ xs: 4, md: 2 }}>
              {displayMode === 'property' && (
                <AuturaDropdown
                  name={'brandingOptions'}
                  label={t('property.data.activeBranding.label', {
                    ns: 'entities',
                  })}
                  options={brandingOptions.map((b) => b.name)}
                  value={brandingOption.name}
                  setValue={handleBrandingOptionChange}
                  helperText={t('helpers.active_branding', {
                    ns: 'properties',
                  })}
                  readonly={!canEdit}
                />
              )}
              <AuturaTextField
                name={`property.data.branding.logoUrl`}
                label={t('property.data.branding.logoUrl', { ns: 'entities' })}
                helperText={t('helpers.logo_url', { ns: 'properties' })}
                disabled={brandingOption.id !== 0 && useOtherContactInfo}
                value={brandingOption.branding.logoUrl}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const updated = { ...branding, logoUrl: e.target.value };
                  setBranding(updated);
                  setBrandingOption({ ...brandingOption, branding: updated });
                }}
                readonly={!canEdit}
              />
              <AuturaTextField
                name={'property.data.branding.bannerLogoUrl'}
                label={t('property.data.branding.bannerLogoUrl', {
                  ns: 'entities',
                })}
                helperText={t('helpers.banner_logo_url', { ns: 'properties' })}
                value={brandingOption.branding.bannerLogoUrl}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const updated = {
                    ...branding,
                    bannerLogoUrl: e.target.value,
                  };
                  setBranding(updated);
                  setBrandingOption({ ...brandingOption, branding: updated });
                }}
                disabled={brandingOption.id !== 0 && useOtherContactInfo}
                endAdornment={
                  <InputAdornment position='end' sx={{ mr: 2 }}>
                    <Slider
                      min={0}
                      max={64}
                      sx={{ width: 100 }}
                      valueLabelDisplay={'on'}
                      color={'primary'}
                      disabled={brandingOption.id !== 0 && useOtherContactInfo}
                      valueLabelFormat={(value) => `Padding: ${value}`}
                      slots={{
                        valueLabel: ({ value, children }) => (
                          <Tooltip placement='top' title={value}>
                            {children}
                          </Tooltip>
                        ),
                      }}
                      aria-label='banner logo padding'
                      value={branding.bannerPadding}
                      onChange={(e, value) =>
                        setBranding({
                          ...branding,
                          bannerPadding: value as number,
                        })
                      }
                    />
                  </InputAdornment>
                }
                readonly={!canEdit}
              />
              {displayMode === 'property' && (
                <AuturaSwitch
                  label={t('labels.address_switch', { ns: 'properties' })}
                  helperText={t('helpers.address_switch', { ns: 'properties' })}
                  name={'addressSwitch'}
                  value={useOtherContactInfo}
                  setValue={handleUseOtherContactInfoChange}
                  disabled={brandingOption.id === 0}
                  readonly={!canEdit}
                />
              )}
              {(displayMode === 'group' || brandingOption.id === 0) && (
                <Stack direction={'row'} spacing={2} alignItems={'center'}>
                  <Typography variant={'body1'}>{`${t('select')} ${t(
                    'property.data.branding.primary',
                    { ns: 'entities' },
                  )}`}</Typography>
                  <ColorPicker
                    color={brandingOption?.branding?.primary || '#000000'}
                    setColor={(value) => {
                      const updated = { ...branding, primary: value };
                      setBranding(updated);
                      setBrandingOption({
                        ...brandingOption,
                        branding: updated,
                      });
                    }}
                    label={''}
                    disabled={brandingOption.id !== 0 || !canEdit}
                  />
                </Stack>
              )}
            </Stack>
            {/* Preview */}
            <Stack direction={'column'} flex={1} gap={4}>
              <FormControl>
                <RadioGroup
                  aria-labelledby='preview-view-radio-group'
                  name='preview-view-radio-group'
                  value={preview}
                  row
                  onChange={handlePreviewChange}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignContent: 'center',
                    gap: 4,
                  }}
                >
                  {state.features.permitService && (
                    <FormControlLabel
                      disabled={!canEdit || !state.features.permitService}
                      value='permitPortal'
                      control={<Radio />}
                      label={t('preview.permit_portal.title', {
                        ns: 'properties',
                      })}
                    />
                  )}
                  {state.tenant.data?.enableFindMyCar && (
                    <FormControlLabel
                      disabled={
                        !canEdit || (entity as Property).data?.disableFindMyCar
                      }
                      value='findMyCar'
                      control={<Radio />}
                      label={t('preview.fmc.title', { ns: 'properties' })}
                    />
                  )}
                </RadioGroup>
              </FormControl>
              <ThemeProvider theme={previewTheme}>
                {preview === 'permitPortal' && state.features.permitService && (
                  <PermitPortalPreview
                    logo={permitPortalPreviewLogo}
                    publicContactInfo={publicContactInfo}
                    entityName={
                      useOtherContactInfo ? brandingOption.name : entity.name
                    }
                  />
                )}
                {preview === 'findMyCar' && (
                  <FMCPreview
                    logo={permitPortalPreviewLogo}
                    publicContactInfo={publicContactInfo}
                    entityName={
                      useOtherContactInfo ? brandingOption.name : entity.name
                    }
                  />
                )}
              </ThemeProvider>
            </Stack>
          </Stack>
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};
